import localforage from 'localforage';
import SentryLogger from './SentryLogger';
import { describePageExit, getPreviousPageExit } from './pageExit';

// MMG-4461: progress trail for the product catalog download, reported once on the next launch if it never
// finished. Healthy devices send nothing. Nothing here may ever throw into, or block, the download.

export const DIAG_HEADER_KEY = 'med-gas-mobile_product-sync-diag';
export const DIAG_CHUNK_KEY_PREFIX = `${DIAG_HEADER_KEY}_c`;
export const DIAG_ATTEMPTS_KEY = `${DIAG_HEADER_KEY}_attempts`;
export const INSTALL_STAMP_KEY = 'med-gas-mobile_install-stamp';
export const DIAG_MAX_REPORTS = 5;
const LOG_PREFIX = '[ProductSyncDiagnostics]:';

export type CatalogDownloadMode = 'full' | 'delta';

export interface IDiagHeader {
    startedAt: string;
    mode: CatalogDownloadMode; // full = 4 worker chunks, delta = one "since" response on the main thread
    trigger: string; // sign-in, retry or job-open
    numChunks: number;
    since?: string;
    appVersion: string;
    userEmail?: string;
    // fresh = no install stamp: first install or the user cleared site data
    storage: 'fresh' | 'existing';
    localDataAgeMs?: number;
    hadDeltaMarker: boolean;
    failedAt?: string;
    error?: string;
    // JS heap samples during the download (Chromium only; WebKit exposes nothing)
    heapSamples?: Array<{ at: string; usedMb: number; totalMb: number; limitMb: number }>;
}

export interface IDiagChunk {
    stage: 'requested' | 'received' | 'parsed' | 'stored' | 'failed';
    elapsedMs?: number | undefined;
    items?: number | undefined;
    responseChars?: number | undefined;
    error?: string | undefined;
}

interface IInstallStamp {
    id: string;
    firstSeen: string;
}

// Resolves once the startup report has read any leftover records, so a new download cannot overwrite them first
let readyResolve: () => void = () => undefined;
let diagnosticsReady: Promise<void> = new Promise(resolve => {
    readyResolve = resolve;
});
let reportStarted = false;

const nowIso = () => new Date().toISOString();

const errorText = (error: unknown): string => {
    if (error instanceof Error) return `${error.name}: ${error.message}`.slice(0, 500);
    try {
        return String(error).slice(0, 500);
    } catch {
        return 'unknown';
    }
};

const chunkKeys = async (): Promise<string[]> => {
    const keys = await localforage.keys();
    return keys.filter(k => k.startsWith(DIAG_CHUNK_KEY_PREFIX));
};

const removeChunkRecords = async (): Promise<void> => {
    const keys = await chunkKeys();
    await Promise.all(keys.map(k => localforage.removeItem(k)));
};

const readInstallStamp = async (): Promise<IInstallStamp | null> => {
    const stamp = (await localforage.getItem(INSTALL_STAMP_KEY)) as IInstallStamp | null;
    return stamp && typeof stamp.firstSeen === 'string' ? stamp : null;
};

const writeInstallStamp = async (): Promise<void> => {
    const id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    await localforage.setItem(INSTALL_STAMP_KEY, { id, firstSeen: nowIso() });
};

const readHeap = (): { usedMb: number; totalMb: number; limitMb: number } | undefined => {
    const memory = typeof performance !== 'undefined' ? (performance as any).memory : undefined;
    if (!memory || !memory.usedJSHeapSize) return undefined;
    return {
        usedMb: Math.round(memory.usedJSHeapSize / 1048576),
        totalMb: Math.round(memory.totalJSHeapSize / 1048576),
        limitMb: Math.round(memory.jsHeapSizeLimit / 1048576)
    };
};

const HEAP_SAMPLE_MS = 5000;
const HEAP_SAMPLE_MAX = 60;
let heapTimer: ReturnType<typeof setInterval> | undefined;
let heapRun = 0; // bumped on stop so a tick already past its read does not write stale data

const stopHeapSampling = () => {
    if (heapTimer !== undefined) clearInterval(heapTimer);
    heapTimer = undefined;
    heapRun++;
};

const startHeapSampling = () => {
    stopHeapSampling();
    if (!readHeap()) return;
    const run = heapRun;
    heapTimer = setInterval(async () => {
        try {
            const heap = readHeap();
            const header = (await localforage.getItem(DIAG_HEADER_KEY)) as IDiagHeader | null;
            if (!heap || !header || run !== heapRun) {
                if (run === heapRun) stopHeapSampling();
                return;
            }
            const samples = header.heapSamples || [];
            samples.push({ at: nowIso(), ...heap });
            header.heapSamples = samples.slice(-HEAP_SAMPLE_MAX);
            await localforage.setItem(DIAG_HEADER_KEY, header);
        } catch {
            stopHeapSampling();
        }
    }, HEAP_SAMPLE_MS);
};

export const beginCatalogDownload = async (options: {
    mode: CatalogDownloadMode;
    trigger: string;
    numChunks: number;
    hadDeltaMarker: boolean;
    since?: string | null;
    userEmail?: string | null;
}): Promise<void> => {
    try {
        // Never hold the download hostage: if the startup report has not finished in 2s, go ahead anyway
        await Promise.race([diagnosticsReady, new Promise<void>(resolve => setTimeout(resolve, 2000))]);

        const existing = (await localforage.getItem(DIAG_HEADER_KEY)) as IDiagHeader | null;
        if (existing && !existing.failedAt && existing.mode !== options.mode) {
            console.info(`${LOG_PREFIX} ${options.mode} download started while a ${existing.mode} download is in flight - not tracked`);
            return;
        }

        const stamp = await readInstallStamp();
        const header: IDiagHeader = {
            startedAt: nowIso(),
            mode: options.mode,
            trigger: options.trigger,
            numChunks: options.numChunks,
            appVersion: process.env.REACT_APP_VERSION || '0.0.0',
            storage: stamp ? 'existing' : 'fresh',
            hadDeltaMarker: options.hadDeltaMarker
        };
        if (options.userEmail) header.userEmail = options.userEmail;
        if (options.since) header.since = String(options.since);
        if (stamp) {
            const age = Date.now() - new Date(stamp.firstSeen).getTime();
            if (Number.isFinite(age)) header.localDataAgeMs = age;
        }

        await removeChunkRecords();
        await localforage.setItem(DIAG_HEADER_KEY, header);
        if (!stamp) await writeInstallStamp();
        startHeapSampling();
    } catch (error) {
        console.warn(`${LOG_PREFIX} begin skipped`, error);
    }
};

// Main-thread stage record (the delta path); the worker writes its own for the 4 full-download chunks
export const markCatalogStage = async (
    mode: CatalogDownloadMode,
    chunk: string,
    stage: IDiagChunk['stage'],
    extra: Partial<Omit<IDiagChunk, 'stage'>> = {}
): Promise<void> => {
    try {
        const header = (await localforage.getItem(DIAG_HEADER_KEY)) as IDiagHeader | null;
        if (!header || header.mode !== mode) return;
        const elapsedMs = Date.now() - new Date(header.startedAt).getTime();
        const record: IDiagChunk = { stage, ...extra };
        if (Number.isFinite(elapsedMs)) record.elapsedMs = elapsedMs;
        await localforage.setItem(`${DIAG_CHUNK_KEY_PREFIX}${chunk}`, record);
    } catch (error) {
        console.warn(`${LOG_PREFIX} stage write skipped`, error);
    }
};

// Size of the raw JSON string axios already holds - no extra copy is made
export const responseChars = (resp: unknown): number | undefined => {
    try {
        const text = (resp as any)?.request?.responseText;
        return typeof text === 'string' ? text.length : undefined;
    } catch {
        return undefined;
    }
};

export const markCatalogDownloadFailed = async (mode: CatalogDownloadMode, error: unknown): Promise<void> => {
    try {
        const header = (await localforage.getItem(DIAG_HEADER_KEY)) as IDiagHeader | null;
        if (!header || header.mode !== mode) return;
        stopHeapSampling();
        header.failedAt = nowIso();
        header.error = errorText(error);
        await localforage.setItem(DIAG_HEADER_KEY, header);
    } catch (err) {
        console.warn(`${LOG_PREFIX} mark failed skipped`, err);
    }
};

export const completeCatalogDownload = async (mode: CatalogDownloadMode): Promise<void> => {
    try {
        const header = (await localforage.getItem(DIAG_HEADER_KEY)) as IDiagHeader | null;
        if (header && header.mode !== mode) return; // the other path's trail is still live
        stopHeapSampling();
        await localforage.removeItem(DIAG_HEADER_KEY);
        await localforage.removeItem(DIAG_ATTEMPTS_KEY);
        await removeChunkRecords();
    } catch (error) {
        console.warn(`${LOG_PREFIX} complete skipped`, error);
    }
};

const mb = (bytes: number | undefined | null): string | undefined =>
    typeof bytes === 'number' && Number.isFinite(bytes) ? `${(bytes / 1048576).toFixed(1)} MB` : undefined;

// Only what helps explain a page kill; Sentry already records browser, OS and user agent. Every probe is guarded.
export const collectDeviceInfo = async (): Promise<Record<string, unknown>> => {
    const info: Record<string, unknown> = {};
    const probe = (name: string, read: () => unknown) => {
        try {
            const value = read();
            if (value !== undefined && value !== null) info[name] = value;
        } catch {
            // the browser does not expose it - fine
        }
    };
    const nav = typeof navigator !== 'undefined' ? (navigator as any) : undefined;
    const perf = typeof performance !== 'undefined' ? (performance as any) : undefined;

    probe('deviceMemory', () => (nav?.deviceMemory ? `${nav.deviceMemory} GB (Chromium hint)` : undefined));
    probe('cpuCores', () => nav?.hardwareConcurrency);
    probe('jsHeapNow', () => {
        const heap = readHeap();
        return heap ? `${heap.usedMb} MB used of ${heap.limitMb} MB limit` : undefined;
    });
    probe('screen', () =>
        typeof screen !== 'undefined' ? `${screen.width}x${screen.height} @${window.devicePixelRatio || 1}x` : undefined
    );
    probe('openedFromHomeScreen', () => nav?.standalone); // iOS only
    probe('pageLoadType', () => perf?.getEntriesByType?.('navigation')?.[0]?.type); // reload vs navigate
    probe('tabWasDiscarded', () => (typeof document !== 'undefined' ? (document as any).wasDiscarded : undefined));
    probe('network', () => {
        const c = nav?.connection;
        return c ? `${c.effectiveType || '?'}, ${c.downlink ?? '?'} Mbps` : undefined;
    });

    // Storage headroom. On Chrome the quota tracks free disk space, so it doubles as a "how full is the device" hint.
    try {
        if (nav?.storage?.estimate) {
            const estimate = await nav.storage.estimate();
            info.siteStorage = `${mb(estimate.usage)} used of ${mb(estimate.quota)} quota`;
        }
    } catch {
        // not available
    }

    // Exact model / OS version where Chromium exposes them (Android, Windows); Safari gives nothing beyond the UA
    try {
        if (nav?.userAgentData?.getHighEntropyValues) {
            const ua = await nav.userAgentData.getHighEntropyValues(['model', 'platformVersion']);
            if (ua?.model) info.model = ua.model;
            if (ua?.platformVersion) info.osVersion = ua.platformVersion;
        }
    } catch {
        // not available
    }

    return info;
};

// Baseline load on this device, counted from the state already in memory - nothing is re-read or serialized,
// because on a phone that just died a second copy of a 60 MB slice could be the last straw
const countRecords = (value: unknown): number => {
    if (Array.isArray(value)) return value.length;
    if (!value || typeof value !== 'object') return 0;
    const props = Object.values(value as Record<string, unknown>);
    const nested = props.filter(v => v && typeof v === 'object');
    if (nested.length === 0) return props.length;
    return nested.reduce<number>((sum, v) => sum + (Array.isArray(v) ? v.length : Object.keys(v as object).length), 0);
};

export const collectLocalDataFootprint = async (getState?: () => unknown): Promise<Record<string, unknown>> => {
    const footprint: Record<string, unknown> = {};

    try {
        const state = getState ? getState() : undefined;
        if (state && typeof state === 'object') {
            const counts = Object.entries(state as Record<string, unknown>)
                .map(([slice, value]) => [slice, countRecords(value)] as [string, number])
                .filter(([, n]) => n > 0)
                .sort((x, y) => y[1] - x[1])
                .slice(0, 5);
            // the five heaviest slices are what matter; the rest are noise
            footprint.largestSlices = Object.fromEntries(counts.map(([slice, n]) => [slice, `${n.toLocaleString()} record${n === 1 ? '' : 's'}`]));
        }
    } catch {
        // state unavailable
    }

    try {
        if (typeof indexedDB !== 'undefined') {
            footprint.materialsStored = await new Promise<number | string>(resolve => {
                const timer = setTimeout(() => resolve('timeout'), 5000);
                const request = indexedDB.open('my-med-gas-mobile');
                request.onerror = () => {
                    clearTimeout(timer);
                    resolve('unavailable');
                };
                request.onupgradeneeded = () => {
                    // the store does not exist yet on this device; do not create anything
                    request.transaction?.abort();
                };
                request.onsuccess = () => {
                    const db = request.result;
                    try {
                        if (!db.objectStoreNames.contains('materials')) {
                            db.close();
                            clearTimeout(timer);
                            resolve(0);
                            return;
                        }
                        const count = db.transaction('materials', 'readonly').objectStore('materials').count();
                        count.onsuccess = () => {
                            db.close();
                            clearTimeout(timer);
                            resolve(count.result);
                        };
                        count.onerror = () => {
                            db.close();
                            clearTimeout(timer);
                            resolve('unavailable');
                        };
                    } catch {
                        db.close();
                        clearTimeout(timer);
                        resolve('unavailable');
                    }
                };
            });
        }
    } catch {
        // not available
    }

    return footprint;
};

// getState: the rehydrated redux state, used only for record counts
export const reportUnfinishedCatalogDownload = async (getState?: () => unknown): Promise<void> => {
    if (reportStarted) return;
    reportStarted = true;

    try {
        const header = (await localforage.getItem(DIAG_HEADER_KEY)) as IDiagHeader | null;
        if (!header) return;

        const keys = await chunkKeys();
        const chunks: Record<string, IDiagChunk> = {};
        for (const key of keys) {
            const chunk = (await localforage.getItem(key)) as IDiagChunk | null;
            if (chunk) chunks[key.slice(DIAG_CHUNK_KEY_PREFIX.length)] = chunk;
        }

        const previousAttempts = Number((await localforage.getItem(DIAG_ATTEMPTS_KEY)) || 0);
        const attempts = (Number.isFinite(previousAttempts) ? previousAttempts : 0) + 1;
        await localforage.setItem(DIAG_ATTEMPTS_KEY, attempts);

        const chunkFailed = Object.values(chunks).some(c => c.stage === 'failed');
        const outcome: 'failed' | 'vanished' = header.failedAt || chunkFailed ? 'failed' : 'vanished';

        // One readable line per chunk: "received 72,577 items (41.2 MB) after 9.1s"
        const describeChunk = (c: IDiagChunk): string => {
            const parts: string[] = [c.stage];
            if (typeof c.items === 'number') parts.push(`${c.items.toLocaleString()} items`);
            if (typeof c.responseChars === 'number') parts.push(`(${mb(c.responseChars)})`);
            if (typeof c.elapsedMs === 'number') parts.push(`after ${(c.elapsedMs / 1000).toFixed(1)}s`);
            if (c.error) parts.push(`- ${c.error}`);
            return parts.join(' ');
        };
        const chunkLines = Object.fromEntries(
            Object.entries(chunks)
                .sort(([x], [y]) => x.localeCompare(y))
                .map(([k, c]) => [k === 'delta' ? 'delta' : `chunk ${k}`, describeChunk(c)])
        );

        const device = await collectDeviceInfo();
        const localData = await collectLocalDataFootprint(getState);
        const localDataAge =
            header.storage === 'fresh'
                ? 'fresh (first install or site data cleared)'
                : `existing, ${Math.round((header.localDataAgeMs || 0) / 60000)} min old`;
        const heapPeak = (header.heapSamples || []).reduce((max, h) => Math.max(max, h.usedMb), 0);
        const pageExit = describePageExit(getPreviousPageExit());

        const summary =
            `${header.mode} download (${header.trigger}) started ${header.startedAt} ` +
            `${outcome === 'vanished' ? 'vanished without a recorded failure' : 'failed'}; ` +
            `attempt ${attempts}; page ended by: ${pageExit}; local data ${localDataAge}; ` +
            `${localData.materialsStored ?? '?'} materials on device; site storage ${device.siteStorage ?? 'unknown'}` +
            (heapPeak ? `; heap peaked at ${heapPeak} MB during the download` : '');

        const { userEmail, heapSamples, ...headerRest } = header;
        const payload = {
            summary,
            outcome,
            attempts,
            pageEndedBy: pageExit,
            chunks: chunkLines,
            download: {
                ...headerRest,
                localData: localDataAge,
                heapSamples: heapSamples?.map(h => `${h.at.slice(11, 19)}Z ${h.usedMb} MB used / ${h.limitMb} MB limit`)
            },
            device,
            localData
        };

        const mode = header.mode || 'full';
        console.info(`${LOG_PREFIX} previous ${mode} product download did not finish (${outcome})`, payload);

        if (attempts <= DIAG_MAX_REPORTS) {
            SentryLogger.logWarning(
                'ProductSyncDiagnostics',
                `Product catalog ${mode} download did not finish (${outcome})`,
                payload,
                userEmail
            );
        }

        await localforage.removeItem(DIAG_HEADER_KEY);
        await removeChunkRecords();
    } catch (error) {
        console.warn(`${LOG_PREFIX} report skipped`, error);
    } finally {
        readyResolve();
    }
};

// Test hook: lets the suite run the startup report more than once.
export const resetProductSyncDiagnosticsForTests = () => {
    reportStarted = false;
    diagnosticsReady = new Promise(resolve => {
        readyResolve = resolve;
    });
};
